/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Paper,
  Toolbar,
  IconButton,
  AppBar,
  Avatar,
  Switch,
  Box,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import PolicyIcon from "@mui/icons-material/Policy";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CircularProgress from "@mui/material/CircularProgress";
import PaymentsIcon from "@mui/icons-material/Payments";
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import PersonIcon from "@mui/icons-material/Person";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LockResetIcon from "@mui/icons-material/LockReset";
import ShareIcon from "@mui/icons-material/Share";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InsightsIcon from "@mui/icons-material/Insights";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Atras from "./atras";
import { auth, uploadImage, updateUserProfile } from "../firebase";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { endSession, isLoggedIn } from "../session";
import { useNavigate } from "react-router-dom";
import BottomNav from "./BottomNav";
import { useThemeMode } from "./ThemeModeContext";
import { useSubscriptionContext } from "./SubscriptionContext";
import { Snackbar, Alert } from "@mui/material";
import { MembershipBadge } from "./membershipTiers";

const ProfileRow = ({ href, onClick, icon, label, color }) => (
  <Container
    style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}
  >
    <Paper
      component={href ? "a" : "div"}
      href={href}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        width: "96%",
        minHeight: "60px",
        padding: "10px",
        marginTop: "20px",
        textDecoration: "none",
        color: color || "inherit",
        cursor: "pointer",
        "&:hover": { backgroundColor: "action.hover" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mr: 1 }}>
        {icon}
      </Box>
      <Typography variant="h6">{label}</Typography>
    </Paper>
  </Container>
);

const Perfil = () => {
  const { mode, toggleMode } = useThemeMode();
  const { subscription } = useSubscriptionContext();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [uid, setUid] = useState("");
  const [loading, setLoading] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(null); // Estado para la URL de la imagen de perfil
  const [icon, setIcon] = useState(null);
  const [iconM, setIconM] = useState(null);
  const [userType, setUserType] = useState(null);
  const [contactInfoStrikes, setContactInfoStrikes] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);

  const handleShareProfile = async () => {
    const profileUrl = `${window.location.origin}/freelancer/${uid}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Mi perfil en Skach", url: profileUrl });
        return;
      } catch (err) {
        // Si el usuario cancela el share nativo, caemos al portapapeles
      }
    }
    try {
      await navigator.clipboard.writeText(profileUrl);
      setLinkCopied(true);
    } catch (err) {
      console.error("Error copying profile link:", err);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const { uid } = user;
          const db = getFirestore();
          const userDocRef = doc(db, "users", uid);

          const userDocSnapshot = await getDoc(userDocRef);

          if (userDocSnapshot.exists()) {
            const userData = userDocSnapshot.data();
            const userName = userData.displayName || "Usuario";
            const userEmail = userData.email || "";
            const uid = userData.uid || "";
            setEmail(userEmail);
            setDisplayName(userName, userEmail);
            setUid(uid);
            if (userData.profileImageUrl) {
              setProfileImageUrl(userData.profileImageUrl);
            }
            setContactInfoStrikes(userData.contactInfoStrikes || 0);

            const userType = userData.userType;
            setUserType(userType || null);
            if (userType === "freelancer") {
              const iconM = (
                <ProfileRow href="/subscription" icon={<PaymentsIcon />} label="Suscripcion" />
              );
              setIcon(icon);
              setIconM(iconM);
            } else {
              setIcon(null); // Limpiar el icono si el usuario no es una empresa
            }
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        // Si el usuario cierra sesión, podemos limpiar el estado del displayName y de la imagen de perfil
        setDisplayName("");
        setEmail("");
        setProfileImageUrl(null);
        setIcon(null);
      }
    });

    return unsubscribe;
  }, );

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    try {
      // Activar el indicador de progreso
      setLoading(true);

      // Subir la imagen al almacenamiento y obtener la URL de descarga
      const imageUrl = await uploadImage(file);

      // Obtener el usuario actual
      const user = auth.currentUser;
      if (user) {
        const uid = user.uid;

        // Actualizar la URL de la imagen de perfil en la base de datos
        await updateUserProfile(uid, { profileImageUrl: imageUrl });

        // Actualizar el estado con la nueva URL de la imagen de perfil
        setProfileImageUrl(imageUrl);
      }

      // Desactivar el indicador de progreso después de completar la carga
      setLoading(false);
    } catch (error) {
      console.error("Error uploading image:", error);
      // En caso de error, desactivar el indicador de progreso
      setLoading(false);
    }
  };

  let navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/login");
    }
  }, [navigate]);

  const onLogout = () => {
    endSession();
    navigate("/login");
  };

  const styles = {
    user: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "flex-start",
      width: "100%",
      height: "90px",
      marginTop: "10px",
    },
    progress: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: "-20px",
      marginLeft: "-20px",
      color: "text.primary",
    },
    uploadButton: {
      marginTop: "20px",
      color: "text.primary",
    },
  };

  return (
    <Container>
      <AppBar position="fixed">
        <Toolbar
          sx={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            padding: "10px",
          }}
        >
          <Atras color="inherit" />
          {profileImageUrl && (
            <Avatar
              sx={{
                marginLeft: 2,
                padding: "0px",
                border: "2.5px solid",
                borderColor: "background.paper",
              }}
              alt="Perfil"
              src={profileImageUrl}
            />
          )}
          <div style={{ position: "relative" }}>
            <input
              type="file"
              accept="image/*"
              onClick={(event) => (event.target.value = null)}
              onChange={handleImageUpload}
              style={{ display: "none" }}
              id="upload-button"
            />
            <IconButton
              variant="contained"
              component="span"
              sx={styles.uploadButton}
              onClick={() => document.getElementById("upload-button").click()}
            >
              <AddPhotoAlternateIcon />
              {loading && (
                <CircularProgress
                  size={40}
                  thickness={4}
                  sx={styles.progress}
                />
              )}
            </IconButton>
          </div>
          <Container sx={styles.user}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h5" color="text.primary">
                {displayName}
              </Typography>
              {userType === "freelancer" && <MembershipBadge subscription={subscription} />}
            </Box>
            <Typography
              variant="h6"
              sx={{ fontSize: "12px", color: "text.secondary" }}
              gutterBottom
            >
              {email}
            </Typography>
          </Container>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 17, mb: 14 }}>
        {contactInfoStrikes > 0 && (
          <Container
            style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}
          >
            <Paper
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "96%",
                padding: "12px",
                marginTop: "20px",
                border: "1px solid",
                borderColor: "warning.main",
                backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(237,108,2,0.12)" : "rgba(237,108,2,0.08)",
              }}
            >
              <WarningAmberIcon color="warning" />
              <Typography variant="body2">
                Tenés {contactInfoStrikes} aviso{contactInfoStrikes !== 1 ? "s" : ""} registrado{contactInfoStrikes !== 1 ? "s" : ""} por compartir información de contacto en el chat. Una infracción más puede derivar en la eliminación de tu cuenta.
              </Typography>
            </Paper>
          </Container>
        )}
        <Container
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Paper
            onClick={toggleMode}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              width: "96%",
              height: "60px",
              padding: "10px",
              marginTop: "20px",
              cursor: "pointer",
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            <Typography
              variant="h6"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mr: 1, color: "text.primary" }}>
                {mode === "dark" ? <DarkModeIcon /> : <LightModeIcon />}
              </Box>
              Tema {mode === "dark" ? "oscuro" : "claro"}
            </Typography>
            <Switch checked={mode === "dark"} onChange={toggleMode} onClick={(e) => e.stopPropagation()} />
          </Paper>
        </Container>
        <ProfileRow href="/edit" icon={<SettingsIcon />} label="Editar Perfil" />
        {userType === "freelancer" && uid && (
          <ProfileRow href={`/freelancer/${uid}`} icon={<PersonIcon />} label="Ver mi perfil público" />
        )}
        {userType === "freelancer" && uid && (
          <ProfileRow onClick={handleShareProfile} icon={<ShareIcon />} label="Compartir mi perfil" />
        )}
        {userType === "freelancer" && (
          <ProfileRow href="/skillTest" icon={<EmojiEventsIcon />} label="Examen de habilidades" />
        )}
        <div>{iconM}</div>
        <div>{icon}</div>
        <ProfileRow href="/activity" icon={<InsightsIcon />} label="Mi actividad" />
        <ProfileRow href="/payments" icon={<ReceiptLongIcon />} label="Mis pagos" />
        <ProfileRow href="/notificationPreferences" icon={<NotificationsActiveIcon />} label="Notificaciones" />
        <ProfileRow href="/changePassword" icon={<LockResetIcon />} label="Cambiar contraseña" />
        <ProfileRow href="/help" icon={<HelpOutlineIcon />} label="Ayuda y Soporte" />
        <ProfileRow href="/terms" icon={<AutoStoriesIcon />} label="Términos de Servicio" />
        <ProfileRow href="/privacyPolicy" icon={<PolicyIcon />} label="Política y Privacidad" />
        <ProfileRow href="/deleteAccount" icon={<DeleteForeverIcon color="error" />} label="Eliminar cuenta" color="error.main" />
        <ProfileRow onClick={onLogout} icon={<ExitToAppIcon color="error" />} label="Cerrar sesión" color="error.main" />
      </Container>
      <Snackbar
        open={linkCopied}
        autoHideDuration={3000}
        onClose={() => setLinkCopied(false)}
        message="Link de tu perfil copiado"
      />
      <BottomNav/>
    </Container>
  );
};

export default Perfil;
